import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { Observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { Importer } from '@feathr/blackbox';
import type { ITanStackColumnMeta } from '@feathr/components';
import { DateTimeCell, LinkNameCell, NumberCell, TanStackHeader } from '@feathr/components';
import { useLocalUrl } from '@feathr/extender/state';

import { getImportType, getRowTotal, getUploadCountTooltip } from './DataImportsPage.utils';
import ImporterStateChip from './ImporterStateChip';
import ImportProgressCell from './ImportProgressCell';
import type { TImportType } from './ImportsFilters';
import optionsColumn from './optionsColumn';

type TCountField = 'rows' | 'inserted' | 'duplicates' | 'skipped';

function countColumn(id: TCountField, title: string, tooltip: string): ColumnDef<Importer> {
  return {
    accessorFn: (row): number => row.get(id) ?? 0,
    cell: ({ row }: CellContext<Importer, unknown>): JSX.Element => (
      <Observer>{(): JSX.Element => <NumberCell value={row.original.get(id)} />}</Observer>
    ),
    header: (): JSX.Element => <TanStackHeader title={title} tooltip={tooltip} />,
    id,
    meta: { align: 'right' } satisfies ITanStackColumnMeta,
    sortDescFirst: false,
  };
}

/** Sorts by `rows`, not the total it shows, so a finished run can look out of order. */
function totalRecordsColumn(t: TFunction): ColumnDef<Importer> {
  return {
    accessorFn: (row): number => row.get('rows') ?? 0,
    cell: ({ row }: CellContext<Importer, unknown>): JSX.Element => (
      <Observer>
        {(): JSX.Element => {
          const rowTotal = getRowTotal(row.original);
          return <NumberCell tooltip={getUploadCountTooltip(t, rowTotal)} value={rowTotal.value} />;
        }}
      </Observer>
    ),
    // Placeholder copy, pending Product and Design.
    header: (): JSX.Element => (
      <TanStackHeader
        title={t('Total Records')}
        tooltip={t(
          'Number of records the import read from the file, not counting blank rows. Until the import finishes, the number found in the uploaded file.',
        )}
      />
    ),
    id: 'rows',
    meta: { align: 'right' } satisfies ITanStackColumnMeta,
    sortDescFirst: false,
  };
}

function createdColumn(t: TFunction): ColumnDef<Importer> {
  return {
    // Created time stamp is extracted from id as defined in MongoDB
    accessorFn: (row): string => row.id,
    cell: ({ row }: CellContext<Importer, unknown>): JSX.Element => (
      <Observer>
        {(): JSX.Element => <DateTimeCell value={row.original.dateCreated.toISOString()} />}
      </Observer>
    ),
    header: (): JSX.Element => <TanStackHeader title={t('Created')} />,
    id: 'id',
    meta: { align: 'center' } satisfies ITanStackColumnMeta,
    // The table opens sorted newest-first, so the first click flips to oldest-first.
    sortDescFirst: true,
  };
}

/**
 * When a run finished, whether it completed or failed. A complete import that finished before the
 * importer recorded `date_completed` falls back to its created date. A failed import without one,
 * and every other state, shows a dash.
 */
function finishedOnColumn(t: TFunction): ColumnDef<Importer> {
  return {
    accessorFn: (row): string | undefined => row.get('date_completed'),
    cell: ({ row }: CellContext<Importer, unknown>): JSX.Element => (
      <Observer>
        {(): JSX.Element => {
          const importer = row.original;
          const state = importer.get('state');
          const dateCompleted = importer.get('date_completed');
          let value: string | undefined;
          if (state === 'complete') {
            value = dateCompleted ?? importer.dateCreated.toISOString();
          } else if (state === 'failed') {
            value = dateCompleted;
          }
          return <DateTimeCell emptyText={'—'} value={value} />;
        }}
      </Observer>
    ),
    header: (): JSX.Element => <TanStackHeader title={t('Finished on')} />,
    id: 'date_completed',
    meta: { align: 'center' } satisfies ITanStackColumnMeta,
    // The table opens sorted newest-first, so the first click flips to oldest-first.
    sortDescFirst: true,
  };
}

/**
 * Column definitions for the `/data/imports` table.
 *
 * Each column `id` is the field the list route orders by, so a renamed id stops sorting. Sorting
 * is server-side; the `accessorFn`s only make TanStack treat a column as sortable.
 *
 * With `isRevampEnabled`, the Finished on column (ordered by `date_completed`) replaces Created,
 * Total Records shows `getRowTotal`, and the table also lists partner imports, so the count
 * headers stop saying "Persons".
 *
 * Every cell reads the model through `Observer`: `TanStackTable` invokes these callbacks inside
 * its own body, so the page's `observer` does not track them.
 */
export default function dataImportsColumns(
  t: TFunction,
  isRevampEnabled = false,
): Array<ColumnDef<Importer>> {
  const typeLabels: Record<TImportType, string> = {
    'opt-out': t('Opt-out import'),
    partner: t('Partner import'),
    person: t('Person import'),
  };

  // Placeholder copy for the all-kinds table, pending Product and Design.
  const countLabels = isRevampEnabled
    ? {
        duplicates: t('Records Updated'),
        inserted: t('Records Created'),
        skipped: t('Records Skipped'),
      }
    : {
        duplicates: t('Persons Updated'),
        inserted: t('Persons Created'),
        skipped: t('Persons Skipped'),
      };
  const countTooltips = isRevampEnabled
    ? {
        duplicates: t('Number of existing records that were matched and updated.'),
        inserted: t('New records that were created from this import.'),
        skipped: t(
          'Number of records that were not imported, because of missing or improperly formatted data, or because the import failed before reaching them.',
        ),
      }
    : {
        duplicates: t('Number of existing contacts that were matched and updated.'),
        inserted: t('New contacts that were created from this import.'),
        skipped: t(
          'Number of records that were not imported, because of a missing or improperly formatted email address, or because the import failed before reaching them.',
        ),
      };

  // Not flex: sized by the cell's fixed-width bar, so the Import column alone takes the spare width.
  const progressColumn: ColumnDef<Importer> = {
    // `progress` is computed on read, so the API can't sort on it.
    cell: ({ row }: CellContext<Importer, unknown>): JSX.Element => (
      <ImportProgressCell importer={row.original} />
    ),
    enableSorting: false,
    header: (): JSX.Element => <TanStackHeader title={t('Progress')} />,
    id: 'progress',
  };

  return [
    {
      accessorFn: (row): string => row.name,
      cell: ({ row }: CellContext<Importer, unknown>): JSX.Element => (
        <Observer>
          {function useAnonymousFunction(): JSX.Element {
            const localUrl = useLocalUrl();

            return (
              <LinkNameCell label={row.original.name} to={localUrl(row.original.getItemUrl())} />
            );
          }}
        </Observer>
      ),
      header: (): JSX.Element => <TanStackHeader title={t('Import')} />,
      id: 'name',
      meta: { isFlex: true, isPrimary: true } satisfies ITanStackColumnMeta,
      sortDescFirst: false,
    },
    {
      accessorFn: (row): string => row.get('state'),
      cell: ({ row }: CellContext<Importer, unknown>): JSX.Element => (
        <ImporterStateChip importer={row.original} isRevampEnabled={isRevampEnabled} />
      ),
      header: (): JSX.Element => <TanStackHeader title={t('Status')} />,
      id: 'state',
      meta: { align: 'center' } satisfies ITanStackColumnMeta,
      sortDescFirst: false,
    },
    ...(isRevampEnabled ? [progressColumn] : []),
    {
      accessorFn: (row): boolean => row.isOptOut,
      cell: ({ row }: CellContext<Importer, unknown>): JSX.Element => (
        <Observer>{(): JSX.Element => <>{typeLabels[getImportType(row.original)]}</>}</Observer>
      ),
      header: (): JSX.Element => <TanStackHeader title={t('Type')} />,
      id: 'overrides',
      meta: { align: 'center' } satisfies ITanStackColumnMeta,
      sortDescFirst: false,
    },
    isRevampEnabled ? finishedOnColumn(t) : createdColumn(t),
    isRevampEnabled
      ? totalRecordsColumn(t)
      : countColumn('rows', t('Total Records'), t('Number of records found in the uploaded file.')),
    countColumn('inserted', countLabels.inserted, countTooltips.inserted),
    countColumn('duplicates', countLabels.duplicates, countTooltips.duplicates),
    countColumn('skipped', countLabels.skipped, countTooltips.skipped),
    // The all-kinds columns and the row menu's matrix both come from `isImportsRevampEnabled`.
    optionsColumn(isRevampEnabled),
  ];
}
